/* OBSYN MOTION — animations du thème (vanilla JS, aucune dépendance) */
(() => {
  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  if (reduce) root.classList.add('ob-no-motion');

  /* ---------- Intro ---------- */
  function startIntro() {
    const markReady = () => root.classList.add('ob-ready');
    if (!root.classList.contains('ob-intro')) {
      requestAnimationFrame(() => requestAnimationFrame(markReady));
      return;
    }
    try { sessionStorage.setItem('ob-intro-seen', '1'); } catch (e) {}
    setTimeout(() => {
      root.classList.add('ob-intro-out');
      markReady();
      setTimeout(() => root.classList.remove('ob-intro', 'ob-intro-out'), 1100);
    }, reduce ? 0 : 1750);
  }

  /* ---------- Découpage du texte ---------- */
  function split(el) {
    if (el.dataset.obSplitDone) return;
    el.dataset.obSplitDone = '1';
    let ci = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === 3) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
            const w = document.createElement('span');
            w.className = 'ob-w';
            [...part].forEach((ch) => {
              const c = document.createElement('span');
              c.className = 'ob-c';
              c.style.setProperty('--ci', ci++);
              c.textContent = ch;
              w.appendChild(c);
            });
            frag.appendChild(w);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1) {
          walk(child);
        }
      });
    };
    walk(el);
    el.setAttribute('aria-label', el.textContent.replace(/\s+/g, ' ').trim());
  }

  /* ---------- Apparitions ---------- */
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

  function autoReveal(scope) {
    const cards = scope.querySelectorAll('product-card, .product-card, .resource-list__item, .collection-card, .main-collection-grid > *');
    const seen = new Map();
    cards.forEach((el) => {
      if (el.closest('.ob-section') || el.classList.contains('ob-auto-reveal')) return;
      if (el.parentElement && el.parentElement.closest('product-card, .product-card')) return;
      const p = el.parentElement;
      const n = seen.get(p) || 0;
      seen.set(p, n + 1);
      el.classList.add('ob-auto-reveal');
      el.style.setProperty('--d', (n % 4) * 90);
      io.observe(el);
    });
  }

  /* ---------- Vidéos (hors hero) : lecture seulement si visibles ---------- */
  const vio = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      const v = e.target;
      if (e.isIntersecting) v.play().catch(() => {});
      else v.pause();
    });
  }, { threshold: 0.01 });

  /* ---------- Hero : vidéos enchaînées ---------- */
  const heroes = [];
  function initHero(hero) {
    if (hero.dataset.obInit) return;
    hero.dataset.obInit = '1';
    const panels = [...hero.querySelectorAll('[data-ob-panel]')];
    const bars = [...hero.querySelectorAll('[data-ob-bar]')];
    const vids = panels.map((p) => p.querySelector('video'));
    const title = hero.querySelector('[data-ob-split]');
    const soundBtn = hero.querySelector('[data-ob-sound]');
    const pauseBtn = hero.querySelector('[data-ob-pause]');
    const dur = (parseFloat(hero.dataset.duration) || 6) * 1000;
    const state = { hero, panels, idx: 0, paused: false, visible: true, timer: null, content: hero.querySelector('.ob-hero__content') };

    if (title) {
      split(title);
      title.style.setProperty('--base', '450ms');
      const show = () => title.classList.add('is-in');
      if (root.classList.contains('ob-ready')) show();
      else new MutationObserver((m, obs) => { if (root.classList.contains('ob-ready')) { show(); obs.disconnect(); } })
        .observe(root, { attributes: true, attributeFilter: ['class'] });
    }

    const playActive = () => {
      const v = vids[state.idx];
      if (v && !state.paused && state.visible) v.play().catch(() => {});
    };
    const go = (i) => {
      state.idx = (i + panels.length) % panels.length;
      panels.forEach((p, k) => p.classList.toggle('is-active', k === state.idx));
      bars.forEach((b, k) => { b.classList.toggle('is-active', k === state.idx); b.style.setProperty('--p', k < state.idx ? 1 : 0); });
      vids.forEach((v, k) => {
        if (!v) return;
        if (k === state.idx) { try { v.currentTime = 0; } catch (e) {} playActive(); } else v.pause();
      });
      clearTimeout(state.timer);
      if (!vids[state.idx] && panels.length > 1 && !state.paused) state.timer = setTimeout(() => go(state.idx + 1), dur);
    };

    vids.forEach((v, k) => {
      if (!v) return;
      v.muted = true;
      v.playsInline = true;
      v.loop = panels.length < 2;
      v.addEventListener('ended', () => { if (k === state.idx) go(state.idx + 1); });
      v.addEventListener('timeupdate', () => {
        if (k === state.idx && bars[k] && v.duration) bars[k].style.setProperty('--p', (v.currentTime / v.duration).toFixed(3));
      });
    });
    bars.forEach((b, k) => b.addEventListener('click', () => go(k)));

    if (pauseBtn) pauseBtn.addEventListener('click', () => {
      state.paused = !state.paused;
      pauseBtn.setAttribute('aria-pressed', state.paused);
      pauseBtn.setAttribute('aria-label', state.paused ? 'Play video' : 'Pause video');
      const v = vids[state.idx];
      if (state.paused) { if (v) v.pause(); clearTimeout(state.timer); } else { playActive(); if (!v) go(state.idx); }
    });
    if (soundBtn) {
      if (!vids.some(Boolean)) soundBtn.hidden = true;
      soundBtn.addEventListener('click', () => {
        const on = soundBtn.getAttribute('aria-pressed') !== 'true';
        soundBtn.setAttribute('aria-pressed', on);
        soundBtn.setAttribute('aria-label', on ? 'Sound off' : 'Sound on');
        vids.forEach((v) => { if (v) v.muted = !on; });
        playActive();
      });
    }

    new IntersectionObserver((en) => {
      state.visible = en[0].isIntersecting;
      const v = vids[state.idx];
      if (!v) return;
      if (state.visible) playActive(); else v.pause();
    }, { threshold: 0.05 }).observe(hero);

    go(0);
    heroes.push(state);
  }

  /* ---------- Bandeau défilant ---------- */
  const marquees = [];
  function initMarquee(track) {
    if (track.dataset.obInit) return;
    track.dataset.obInit = '1';
    marquees.push({ track, dir: parseFloat(track.dataset.obMarquee) || -1, speed: parseFloat(track.dataset.speed) || 4, x: 0, w: 0 });
  }

  /* ---------- Curseur + boutons magnétiques ---------- */
  function initCursor() {
    if (reduce || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const dot = document.createElement('div');
    const ring = document.createElement('div');
    dot.className = 'ob-cursor';
    ring.className = 'ob-cursor-ring';
    document.body.append(dot, ring);
    let mx = -100, my = -100, rx = -100, ry = -100;
    window.addEventListener('pointermove', (e) => {
      mx = e.clientX; my = e.clientY;
      dot.style.transform = `translate3d(${mx}px, ${my}px, 0)`;
    }, { passive: true });
    document.addEventListener('pointerover', (e) => {
      ring.classList.toggle('is-hover', !!e.target.closest('a, button, [role="button"], input, select, label, summary'));
    });
    const tick = () => {
      rx += (mx - rx) * 0.18; ry += (my - ry) * 0.18;
      ring.style.transform = `translate3d(${rx}px, ${ry}px, 0)`;
      requestAnimationFrame(tick);
    };
    tick();
  }
  function initMagnetic(el) {
    if (reduce || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left - r.width / 2) * 0.28;
      const y = (e.clientY - r.top - r.height / 2) * 0.4;
      el.style.transform = `translate(${x}px, ${y}px)`;
    });
    el.addEventListener('pointerleave', () => { el.style.transform = ''; });
  }

  /* ---------- Boucle de scroll ---------- */
  let lastY = window.scrollY, velocity = 0, lastT = performance.now();
  function frame(now) {
    const y = window.scrollY;
    const vh = window.innerHeight;
    const dt = Math.min(64, now - lastT) || 16;
    lastT = now;
    const instant = (y - lastY) / dt;
    velocity += (instant - velocity) * 0.12;
    lastY = y;

    const max = document.documentElement.scrollHeight - vh;
    root.style.setProperty('--ob-scroll', max > 0 ? (y / max).toFixed(4) : 0);

    if (!reduce) {
      heroes.forEach((h) => {
        const r = h.hero.getBoundingClientRect();
        if (r.bottom < 0) return;
        const p = clamp(-r.top / r.height);
        if (h.content) {
          h.content.style.transform = `translate3d(0, ${p * -90}px, 0)`;
          h.content.style.opacity = 1 - p * 1.3;
        }
        h.panels.forEach((panel) => {
          const inner = panel.firstElementChild;
          if (inner) inner.style.transform = `translate3d(0, ${p * 120}px, 0) scale(${1 + p * 0.06})`;
        });
      });

      document.querySelectorAll('[data-ob-manifesto]').forEach((sec) => {
        const r = sec.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const p = clamp(-r.top / (r.height - vh));
        const words = sec._words || (sec._words = [...sec.querySelectorAll('.ob-word')]);
        const lit = Math.round(p * 1.15 * words.length);
        words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
        sec.style.setProperty('--p', p.toFixed(3));
      });

      document.querySelectorAll('[data-ob-story]').forEach((sec) => {
        const r = sec.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const p = clamp(-r.top / (r.height - vh));
        sec.style.setProperty('--p', p.toFixed(3));
        const frameEl = sec.querySelector('[data-ob-story-frame]');
        if (frameEl && !mqMobile.matches) frameEl.style.setProperty('--ob-frame-scale', (0.86 + clamp((vh - r.top) / vh) * 0.14).toFixed(3));
        const chapters = sec._ch || (sec._ch = [...sec.querySelectorAll('[data-ob-chapter]')]);
        let active = 0;
        chapters.forEach((c, i) => { if (c.getBoundingClientRect().top < vh * 0.55) active = i; });
        chapters.forEach((c, i) => c.classList.toggle('is-active', i === active));
        const idx = sec.querySelector('[data-ob-story-index]');
        if (idx) idx.textContent = String(active + 1).padStart(2, '0');
      });

      document.querySelectorAll('[data-ob-drift]').forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const p = (vh - r.top) / (vh + r.height);
        el.style.setProperty('--x', `${(parseFloat(el.dataset.obDrift) || -1) * p * 40}vw`);
      });
    }

    marquees.forEach((m) => {
      if (!m.track.isConnected) return;
      if (!m.w) m.w = m.track.firstElementChild ? m.track.firstElementChild.offsetWidth : 0;
      if (!m.w) return;
      const base = reduce ? 0 : m.speed * 0.025 * dt;
      const boost = reduce ? 0 : Math.abs(velocity) * dt * 0.6;
      const scrollDir = velocity < -0.02 ? -1 : 1;
      m.x += (base + boost) * m.dir * scrollDir;
      if (m.x <= -m.w) m.x += m.w;
      if (m.x > 0) m.x -= m.w;
      m.track.style.transform = `translate3d(${m.x}px, 0, 0)`;
    });

    requestAnimationFrame(frame);
  }

  /* ---------- Initialisation ---------- */
  function init(scope = document) {
    scope.querySelectorAll('[data-ob-hero]').forEach(initHero);
    scope.querySelectorAll('[data-ob-marquee]').forEach(initMarquee);
    scope.querySelectorAll('[data-ob-split]').forEach((el) => {
      if (el.closest('[data-ob-hero]')) return;
      split(el);
      io.observe(el);
    });
    scope.querySelectorAll('[data-ob-reveal], [data-ob-outro]').forEach((el) => io.observe(el));
    scope.querySelectorAll('[data-ob-magnetic]').forEach(initMagnetic);
    scope.querySelectorAll('.ob-section video').forEach((v) => {
      if (v.closest('[data-ob-hero]')) return;
      v.muted = true; v.playsInline = true; vio.observe(v);
    });
    autoReveal(scope);
  }

  function boot() {
    init();
    initCursor();
    startIntro();
    requestAnimationFrame(frame);
    window.addEventListener('resize', () => marquees.forEach((m) => { m.w = 0; }), { passive: true });
    new MutationObserver(() => autoReveal(document)).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', (e) => init(e.target));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
